import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, User, Mail, CheckCircle, Briefcase, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { authAPI } from '../services/api';

const CompleteProfilePage: React.FC = () => {
  const { user, completeProfile, loading: authLoading, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    phone: user?.phone || '',
    role: (user?.role || 'customer') as 'customer' | 'provider',
    providerType: (user?.skills && user.skills.length > 0 ? user.skills[0] : 'driver'),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [policeVerificationFile, setPoliceVerificationFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>((user as any)?.languagePreference || i18n.language || 'en');

  // Prevent browser back button navigation when profile completion is required
  useEffect(() => {
    if (!user || authLoading) return;

    const needsPhone = !user.phone || user.phone.trim() === '';

    // Only block back button if phone is missing
    if (needsPhone) {
      // Push current state to prevent back navigation
      window.history.pushState(null, '', window.location.href);

      const handlePopState = (event: PopStateEvent) => {
        // Prevent back navigation
        window.history.pushState(null, '', window.location.href);
        alert('Please complete your profile before accessing other parts of the app.');
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [user, authLoading]);

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData({
        phone: user.phone || '',
        role: (user.role || 'customer') as 'customer' | 'provider',
        providerType: (user.skills && user.skills.length > 0 ? user.skills[0] : 'driver'),
      });
    }
  }, [user]);

  // Check if profile is already complete and redirect if needed
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!user) {
      return;
    }

    // Check if user has phone number
    const hasPhone = user.phone && user.phone.trim() !== '';
    const phoneVerified = user.phoneVerified === true;
    
    // For providers, check police verification status
    if (user.role === 'provider') {
      const policeVerificationStatus = user.policeVerificationStatus || 'not_submitted';
      const kycStatus = user.kycStatus || 'not_submitted';
      const hasPoliceVerificationDocument = user.policeVerificationDocument && 
        user.policeVerificationDocument.trim() !== '';

      // NEW FLOW: KYC first, then police verification
      // If user has phone AND phone is verified, check KYC status first
      if (hasPhone && phoneVerified) {
        // KYC not submitted -> go to KYC first
        if (kycStatus === 'not_submitted') {
          navigate('/kyc', { replace: true });
          return;
        }
        
        // KYC rejected -> go to KYC for re-upload
        if (kycStatus === 'rejected') {
          navigate('/kyc', { replace: true });
          return;
        }
        
        // KYC pending -> wait for approval
        if (kycStatus === 'pending') {
          navigate('/review-pending', { replace: true });
          return;
        }
        
        // KYC approved - now check police verification
        // If police verification not submitted or rejected, stay on page to upload
        if (policeVerificationStatus === 'not_submitted' || policeVerificationStatus === 'rejected') {
          // Stay on page - user can upload police verification
          return;
        }
        
        // Police verification pending -> go to review pending
        if (policeVerificationStatus === 'pending') {
          navigate('/review-pending', { replace: true });
          return;
        }
        
        // Both approved -> go to home
        if (kycStatus === 'approved' && policeVerificationStatus === 'approved') {
          navigate('/', { replace: true });
          return;
        }
      }
    } else {
      // For customers, if they have phone AND phone is verified, profile is complete
      if (hasPhone && phoneVerified) {
        navigate('/', { replace: true });
        return;
      }
    }
  }, [user, authLoading, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        setError('Please upload a PDF, JPG, or PNG file');
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setPoliceVerificationFile(file);
      setError('');
    }
  };

  const uploadPoliceVerificationDocument = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('document', file);
    
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/api/auth/upload-police-verification`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      throw new Error('Failed to upload document');
    }
    
    const data = await response.json();
    return data.data.url;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    // Normalize phone number: remove spaces, dashes, parentheses, and other formatting
    const normalizedPhone = formData.phone.trim().replace(/[\s\-\(\)\.]/g, '');
    
    // Ensure phone starts with + for international format, or add country code if missing
    let phoneToSend = normalizedPhone;
    if (!phoneToSend.startsWith('+')) {
      // If it starts with 0, remove it (common in some countries)
      if (phoneToSend.startsWith('0')) {
        phoneToSend = phoneToSend.substring(1);
      }
    }

    // Check if phone is verified
    // If phone number changed or not verified, require OTP verification first
    const phoneChanged = user?.phone !== phoneToSend;
    const phoneNotVerified = !user?.phoneVerified;
    
    if (phoneChanged || phoneNotVerified) {
      // Navigate to phone verification
      navigate('/phone-verification', { 
        state: { 
          phone: phoneToSend, 
          email: user?.email,
          returnTo: '/complete-profile'
        } 
      });
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Only require police verification if it hasn't been uploaded before (or was rejected)
      // Check both the document URL and the status - if document exists and is not rejected, don't require upload
      const hasPoliceVerificationDocument = user?.policeVerificationDocument && 
        user.policeVerificationDocument.trim() !== '';
      const isRejected = user?.policeVerificationStatus === 'rejected';
      
      // Only require police verification if KYC is approved
      // Check KYC status first
      const kycStatus = user?.kycStatus || 'not_submitted';
      if (formData.role === 'provider' && kycStatus !== 'approved') {
        // KYC not approved yet - redirect to KYC
        navigate('/kyc', { replace: true });
        setLoading(false);
        return;
      }
      
      // KYC approved - now require police verification
      if (formData.role === 'provider' && !hasPoliceVerificationDocument && !policeVerificationFile && !isRejected) {
        setError('Please upload your police verification document');
        setLoading(false);
        return;
      }
      
      // If rejected, require new upload
      if (formData.role === 'provider' && isRejected && !policeVerificationFile) {
        setError('Please upload a new police verification document');
        setLoading(false);
        return;
      }

      let policeVerificationDocumentUrl: string | undefined;
      
      // Upload police verification document if provider and file is selected
      // Upload if: no document exists, or document was rejected (re-upload)
      if (formData.role === 'provider' && policeVerificationFile && (!hasPoliceVerificationDocument || isRejected)) {
        setUploadingFile(true);
        try {
          policeVerificationDocumentUrl = await uploadPoliceVerificationDocument(policeVerificationFile);
        } catch (uploadError: any) {
          setError(uploadError.message || 'Failed to upload police verification document');
          setLoading(false);
          setUploadingFile(false);
          return;
        }
        setUploadingFile(false);
      }
      
      // If document already exists and is not rejected, don't send undefined - send the existing document URL
      // This ensures the backend knows the document is already there
      // But if rejected, we want to upload the new file, so don't use the old one
      if (formData.role === 'provider' && hasPoliceVerificationDocument && !policeVerificationDocumentUrl && !isRejected) {
        policeVerificationDocumentUrl = user.policeVerificationDocument;
      }

      // Normalize phone number: remove spaces, dashes, parentheses, and other formatting
      const normalizedPhone = formData.phone.trim().replace(/[\s\-\(\)\.]/g, '');
      
      // Ensure phone starts with + for international format, or add country code if missing
      let phoneToSend = normalizedPhone;
      if (!phoneToSend.startsWith('+')) {
        // If it starts with 0, remove it (common in some countries)
        if (phoneToSend.startsWith('0')) {
          phoneToSend = phoneToSend.substring(1);
        }
      }

      // Check if phone verification is required (only if OTP feature is enabled)
      const phoneChanged = user?.phone !== phoneToSend;
      const phoneNotVerified = !user?.phoneVerified;
      
      // Check if OTP feature is enabled before requiring verification
      try {
        const otpCheckResponse = await authAPI.checkOTPEnabled();
        const isOTPEnabled = otpCheckResponse.success && otpCheckResponse.data.enabled;
        
        // Only require phone verification if OTP feature is enabled
        if (isOTPEnabled && (phoneChanged || phoneNotVerified)) {
          // Navigate to phone verification
          // Store phone temporarily for verification
          navigate('/phone-verification', { 
            state: { 
              phone: phoneToSend, 
              email: user?.email,
              returnTo: '/complete-profile'
            } 
          });
          setLoading(false);
          return;
        }
        // If OTP is disabled, update phone directly without verification
        if (!isOTPEnabled && phoneChanged) {
          // Update phone and mark as verified when OTP is disabled
          user.phone = phoneToSend;
          user.phoneVerified = true;
        }
      } catch (error) {
        console.error('Failed to check OTP feature status:', error);
        // If check fails, proceed without verification (assume disabled to avoid blocking)
        if (phoneChanged) {
          user.phone = phoneToSend;
          user.phoneVerified = true;
        }
      }

      const skills = formData.role === 'provider' ? [formData.providerType] : undefined;
      await completeProfile(phoneToSend, formData.role, skills, policeVerificationDocumentUrl);
      
      // Navigate based on step
      if (formData.role === 'provider' && (policeVerificationDocumentUrl || hasPoliceVerificationDocument)) {
        // After police verification upload, go to review pending
        navigate('/review-pending', { replace: true });
      } else if (formData.role === 'provider') {
        // Initial profile completion - go to KYC
        navigate('/kyc', { replace: true });
      } else {
        // Customer - no KYC needed
        navigate('/', { replace: true });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while auth is loading
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Immediate redirect check: if profile is already complete, redirect now
  // BUT: If rejected, allow re-upload (don't redirect)
  if (!authLoading && user) {
    const hasPhone = user.phone && user.phone.trim() !== '';
    
    if (user.role === 'provider') {
      const policeVerificationStatus = user.policeVerificationStatus || 'not_submitted';
      const kycStatus = user.kycStatus || 'not_submitted';
      const hasPoliceVerificationDocument = user.policeVerificationDocument && 
        user.policeVerificationDocument.trim() !== '';

      // If user has phone AND (police verification submitted OR document exists), profile is complete
      // BUT: If rejected, allow re-upload (don't redirect)
      if (hasPhone && (policeVerificationStatus !== 'not_submitted' || hasPoliceVerificationDocument)) {
        // If rejected, stay on page to allow re-upload
        if (policeVerificationStatus === 'rejected') {
          // Stay on page - user can re-upload
        } else {
          // Show loading while redirect happens
          // Always redirect to KYC if not submitted (even if police verification is pending)
          if (kycStatus === 'not_submitted') {
            // KYC not submitted -> go to KYC (allows both documents to be uploaded before showing pending)
            navigate('/kyc', { replace: true });
          } else if (kycStatus === 'rejected') {
            // KYC rejected -> go to KYC for re-upload
            navigate('/kyc', { replace: true });
          } else {
            // At this point, KYC is either 'pending' or 'approved'
            // Check if both documents are submitted and at least one is pending
            const policeVerificationSubmitted = 
              policeVerificationStatus !== 'not_submitted' || hasPoliceVerificationDocument;
            
            // KYC is already submitted (we're in the else block after checking not_submitted and rejected)
            if (policeVerificationSubmitted && (
              policeVerificationStatus === 'pending' || 
              kycStatus === 'pending' ||
              // If document exists but status is still "not_submitted", it's likely pending review
              (hasPoliceVerificationDocument && policeVerificationStatus === 'not_submitted')
            )) {
              // Both documents submitted but pending -> go to review pending
              navigate('/review-pending', { replace: true });
            } else {
              // Everything approved -> go to home
              navigate('/', { replace: true });
            }
          }
          return (
            <div className="min-h-screen bg-white flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
                <p className="text-gray-300">Profile already complete. Redirecting...</p>
              </div>
            </div>
          );
        }
      }
    } else if (hasPhone) {
      // Customer with phone - profile complete
      navigate('/', { replace: true });
      return (
        <div className="min-h-screen bg-white flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
            <p className="text-gray-600">Profile already complete. Redirecting...</p>
          </div>
        </div>
      );
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
          <p className="text-gray-300 mb-6">You need to be logged in to complete your profile.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-black text-white px-6 py-2 rounded-md hover:bg-gray-800"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-4 sm:space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-900">
            Complete Your Profile
          </h2>
          <p className="mt-2 text-center text-xs sm:text-sm text-gray-600">
            You're almost done! Just provide a few more details to complete your account setup.
          </p>
        </div>

        {/* Language Selection */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-3">
            <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 sm:mr-3" />
            <label htmlFor="language" className="block text-xs sm:text-sm font-medium text-gray-700">
              Select Language / भाषा चुनें
            </label>
          </div>
          <select
            id="language"
            value={selectedLanguage}
            onChange={async (e) => {
              const newLang = e.target.value;
              setSelectedLanguage(newLang);
              try {
                // Change language immediately
                await i18n.changeLanguage(newLang);
                // Update backend if user exists
                if (user) {
                  try {
                    await authAPI.updateProfileData({ languagePreference: newLang });
                    if (updateUser) {
                      await updateUser();
                    }
                  } catch (err) {
                    console.error('Failed to update language preference:', err);
                  }
                }
              } catch (err) {
                console.error('Failed to change language:', err);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-black focus:ring-2 focus:ring-black focus:border-transparent text-xs sm:text-sm"
          >
            <option value="en">English</option>
            <option value="hi">हिंदी (Hindi)</option>
          </select>
        </div>

        {/* User Info Display */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">Your Account Details</h3>
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center">
              <User className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{user.name}</p>
                <p className="text-xs text-gray-500">Full Name</p>
              </div>
            </div>
            <div className="flex items-center">
              <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">{user.email}</p>
                <p className="text-xs text-gray-500">Email Address</p>
              </div>
            </div>
            <div className="flex items-center">
              <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-500">Not provided</p>
                <p className="text-xs text-gray-500">Phone Number</p>
              </div>
            </div>
          </div>
        </div>

        <form className="mt-4 sm:mt-8 space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-3 sm:px-4 py-2 sm:py-3 rounded-md text-xs sm:text-sm">
              {error}
            </div>
          )}
          
          {/* Role Selection */}
          <div>
            <label htmlFor="role" className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              I want to
            </label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <label className={`relative flex items-center justify-center p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.role === 'customer' 
                  ? 'border-black bg-white' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="role"
                  value="customer"
                  checked={formData.role === 'customer'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <User className={`h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 ${
                    formData.role === 'customer' ? 'text-black' : 'text-gray-400'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium ${
                    formData.role === 'customer' ? 'text-black' : 'text-gray-900'
                  }`}>
                    Book Services
                  </span>
                </div>
              </label>

              <label className={`relative flex items-center justify-center p-3 sm:p-4 border-2 rounded-lg cursor-pointer transition-all ${
                formData.role === 'provider' 
                  ? 'border-black bg-white' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="role"
                  value="provider"
                  checked={formData.role === 'provider'}
                  onChange={handleChange}
                  className="sr-only"
                />
                <div className="text-center">
                  <Briefcase className={`h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 ${
                    formData.role === 'provider' ? 'text-black' : 'text-gray-400'
                  }`} />
                  <span className={`text-xs sm:text-sm font-medium ${
                    formData.role === 'provider' ? 'text-black' : 'text-gray-900'
                  }`}>
                    Provide Services
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Provider Type Selection - Only shown if role is provider */}
          {formData.role === 'provider' && (
            <div>
              <label htmlFor="providerType" className="block text-xs sm:text-sm font-medium text-gray-700">
                Service Type
              </label>
              <select
                id="providerType"
                name="providerType"
                value={formData.providerType}
                onChange={handleChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 border border-gray-300 bg-white rounded-md focus:outline-none focus:ring-gray-500 focus:border-black text-xs sm:text-sm"
              >
                <option value="driver">Driver</option>
              </select>
              <p className="mt-2 text-xs sm:text-sm text-gray-500">
                Currently, only driver services are available. More service types coming soon!
              </p>
            </div>
          )}

          {/* Phone Number */}
          <div>
            <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700">
              Phone Number
            </label>
            <div className="mt-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <input
                id="phone"
                name="phone"
                type="tel"
                autoComplete="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                className="appearance-none relative block w-full pl-9 sm:pl-10 pr-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-gray-500 focus:border-black focus:z-10 text-xs sm:text-sm"
                placeholder="Enter your phone number"
              />
            </div>
            <p className="mt-2 text-xs sm:text-sm text-gray-500">
              We'll use this to contact you about your bookings and send important updates.
            </p>
          </div>

          {/* Police Verification Document - Only for providers, only after KYC is approved, and only if not already uploaded or rejected */}
          {formData.role === 'provider' && user?.kycStatus === 'approved' && (!user?.policeVerificationDocument || user?.policeVerificationStatus === 'rejected') && (
            <div>
              <label htmlFor="policeVerification" className="block text-xs sm:text-sm font-medium text-gray-700">
                Police Verification Document <span className="text-red-500">*</span>
              </label>
              <div className="mt-1">
                <input
                  id="policeVerification"
                  name="policeVerification"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="block w-full text-xs sm:text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-white file:text-black hover:file:bg-gray-100"
                  required
                />
              </div>
              <p className="mt-2 text-xs sm:text-sm text-gray-500">
                Upload your police verification document (PDF, JPG, or PNG, max 5MB). This will be reviewed by our admin team.
              </p>
              {policeVerificationFile && (
                <p className="mt-1 text-xs text-green-600">
                  Selected: {policeVerificationFile.name}
                </p>
              )}
            </div>
          )}

          {/* Show existing document if already uploaded (and not rejected) */}
          {formData.role === 'provider' && user?.policeVerificationDocument && user?.policeVerificationStatus !== 'rejected' && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3 sm:p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-xs sm:text-sm text-green-800 font-medium">
                  Police verification document already uploaded
                </p>
              </div>
            </div>
          )}

          {/* Show rejection message if rejected */}
          {formData.role === 'provider' && user?.policeVerificationStatus === 'rejected' && user?.policeVerificationRejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 sm:p-4 mb-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-red-800 font-medium mb-1">
                    Previous submission rejected
                  </p>
                  <p className="text-xs sm:text-sm text-red-700">
                    {user.policeVerificationRejectionReason}
                  </p>
                  <p className="text-xs text-red-600 mt-2">
                    Please upload a new document.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Show KYC rejection notice if KYC is also rejected */}
          {formData.role === 'provider' && user?.kycStatus === 'rejected' && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 sm:p-4 mb-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-amber-800 font-medium mb-1">
                    KYC Documents Also Rejected
                  </p>
                  <p className="text-xs sm:text-sm text-amber-700">
                    Your KYC documents have also been rejected. After you re-upload and get approval for your police verification document, you will be redirected to re-upload your KYC documents.
                  </p>
                  {user.kycRejectionReason && (
                    <p className="text-xs text-amber-600 mt-2">
                      <span className="font-medium">Reason:</span> {user.kycRejectionReason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading || uploadingFile || !formData.phone.trim() || (formData.role === 'provider' && !user?.policeVerificationDocument && !policeVerificationFile && user?.policeVerificationStatus !== 'rejected') || (formData.role === 'provider' && user?.policeVerificationStatus === 'rejected' && !policeVerificationFile)}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-xs sm:text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingFile ? 'Uploading Document...' : loading ? 'Completing Profile...' : 'Complete Profile'}
            </button>
            
            {/* Logout Button */}
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Are you sure you want to logout? You\'ll need to sign in again to continue.')) {
                  logout();
                  navigate('/login');
                }
              }}
              className="w-full flex items-center justify-center gap-2 py-2 px-4 border border-gray-300 text-xs sm:text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>

          <div className="text-center">
            <p className="text-xs sm:text-sm text-gray-600">
              By completing your profile, you agree to our{' '}
              <Link to="/terms-and-conditions" className="font-medium text-black hover:text-gray-700">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy-policy" className="font-medium text-black hover:text-gray-700">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfilePage;

